import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { SITE_NAME, OG_IMAGE, OG_IMAGE_WIDTH, OG_IMAGE_HEIGHT, canonicalUrl } from "@/lib/seo";

/**
 * Renders the full set of SEO tags for a page:
 *   - <title>
 *   - <meta name="description">
 *   - <link rel="canonical">
 *   - Open Graph tags (og:title, og:description, og:url, og:image, og:type)
 *   - Twitter card tags
 *   - Optional <meta name="robots" content="noindex,nofollow">
 *   - Optional JSON-LD structured data (array of plain-JS objects)
 *
 * The component is a thin, purely-declarative wrapper around react-helmet-async.
 * Pages compose it near the top of their tree.
 */
export function SEO({
  title,
  description,
  path,          // override canonical path; defaults to current pathname
  image = OG_IMAGE,
  type = "website",
  noindex = false,
  jsonLd,        // object | array of objects | undefined
}) {
  const loc = useLocation();
  const canonical = canonicalUrl(path ?? loc.pathname);
  const fullTitle = title || SITE_NAME;
  const desc = description || "";
  const ldArray = jsonLd
    ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd])
    : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      {desc && <meta name="description" content={desc} />}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      {desc && <meta property="og:description" content={desc} />}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content={String(OG_IMAGE_WIDTH)} />
      <meta property="og:image:height" content={String(OG_IMAGE_HEIGHT)} />

      {/* Twitter card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {desc && <meta name="twitter:description" content={desc} />}
      <meta name="twitter:image" content={image} />

      {/* Structured data */}
      {ldArray.map((obj, i) => (
        <script
          key={`ld-${i}`}
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
        >{JSON.stringify(obj)}</script>
      ))}
    </Helmet>
  );
}

export default SEO;
